import React, { useState, useEffect, useRef } from 'react';
import styles from './style.module.scss';
import Search from '@mui/icons-material/Search';
import * as AgentActions from 'utils/agent-endpoint';
import { CountryCodeList } from 'types/countries';
import { SuggestionDropdown } from 'components/agent-search';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_CLICK_AGENT_PAGE_NAME_SEARCH_BAR } from 'constants/events';
import { agentSearchIds } from 'constants/test-constants';
import { buildClassName } from 'utils/build-class-name';
import { AgentType, AgentFilters } from 'utils/agent-endpoint';
import CloseIcon from 'components/icon/close-icon';
import { useTranslation } from 'next-i18next';
import { AGENT_SEARCH_NAMESPACE, LOCALE_AGENT_SEARCH_PATHS } from 'types/locales';

interface InputWithSearchIconProps {
  onSelectName: (query: string) => void;
  country: CountryCodeList;
  nameQuery: string;
  isCommercial?: boolean;
  filters?: AgentFilters;
}

export default function InputWithSearchIcon({
  onSelectName,
  country,
  nameQuery,
  isCommercial = false,
  filters = { isMilnet: false },
}: InputWithSearchIconProps) {
  const [names, setNames] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [query, setQuery] = useState(nameQuery);
  const queryRef = useRef(query);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation(AGENT_SEARCH_NAMESPACE);

  // Watch for enter press
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && queryRef.current === '') {
        onSelectName('');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  useEffect(() => {
    setQuery(nameQuery);
  }, [nameQuery]);

  useEffect(() => {
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    const fetchNames = async () => {
      const agentType = isCommercial ? AgentType.COMMERCIAL : AgentType.RESIDENTIAL;
      const data = await AgentActions.nameSuggestions({
        query,
        country,
        agentType,
        filters,
      });

      if (!(data instanceof Error)) {
        if (data) {
          setNames(data);
        } else {
          setNames([]);
        }
      }
    };

    // Debounce API calls - wait 300ms after user stops typing
    if (query.length > 0) {
      debounceTimeoutRef.current = setTimeout(fetchNames, 300);
    } else {
      setNames([]);
    }

    // Cleanup timeout on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, filters]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setQuery(value);
    setShowSuggestions(!!value);
  };

  const handleSelectName = (n: string) => {
    setQuery(n);
    onSelectName(n);
    setNames([]);
    setShowSuggestions(false);
  };

  const handleClearQuery = () => {
    setQuery('');
    setShowSuggestions(false);
    onSelectName('');
  };

  return (
    <div data-testid="input-wrapper" className={buildClassName(styles['input-wrapper'], isCommercial && styles['is-commercial'], query && styles['has-query'])}>
      <div className={styles.dropdown}>
        <div className={styles['search-button']} data-testid={agentSearchIds.agentSearchIcon}>
          <Search style={{ padding: 0, margin: 0 }} />
        </div>
        {/* name suggestion dropdown */}
        {names.length > 0 && showSuggestions && (
          <SuggestionDropdown
            suggestions={names}
            type={'name'}
            onSelect={handleSelectName}
            onClose={() => setShowSuggestions(false)}
          />
        )}
      </div>
      <input
        type="text"
        placeholder={t(LOCALE_AGENT_SEARCH_PATHS.PLACEHOLDERS_NAME, { defaultValue: 'Name' })}
        value={query}
        onChange={handleQueryChange}
        onClick={() => trackEvent(GTM_CLICK_AGENT_PAGE_NAME_SEARCH_BAR)}
        data-testid={agentSearchIds.agentNameInput}
      />
      {query && (
        <CloseIcon testId="clear-button" className={styles['clear-button']} onClick={handleClearQuery} />
      )}
    </div>
  );
}
